import { Title } from '@solidjs/meta'
import type { Component, JSX } from 'solid-js'
import { createSignal, For } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Modal from '~/components/Modal'
import Switch from '~/components/Switch'

const ModalPage: Component = (): JSX.Element => {
  const [isBasicOpen, setIsBasicOpen] = createSignal(false)
  const [isSizeOpen, setIsSizeOpen] = createSignal(false)
  const [selectedSize, setSelectedSize] = createSignal<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fullscreen'>('md')
  const [isLongContentOpen, setIsLongContentOpen] = createSignal(false)
  const [isBackdropToggleOpen, setIsBackdropToggleOpen] = createSignal(false)
  const [closeOnBackdrop, setCloseOnBackdrop] = createSignal(true)
  const [isFormOpen, setIsFormOpen] = createSignal(false)
  const [isFullscreenOpen, setIsFullscreenOpen] = createSignal(false)

  const [formData, setFormData] = createSignal({
    name: '',
    email: ''
  })

  const sizes: { label: string; value: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fullscreen' }[] = [
    { label: 'Extra Pequeño', value: 'xs' },
    { label: 'Pequeño', value: 'sm' },
    { label: 'Mediano', value: 'md' },
    { label: 'Grande', value: 'lg' },
    { label: 'Extra Grande', value: 'xl' },
    { label: 'Ancho Completo', value: 'full' },
    { label: 'Pantalla Completa', value: 'fullscreen' }
  ]

  return (
    <>
      <Title>Modal Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Modal</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Ventanas de diálogo altamente personalizables para mostrar contenido superpuesto, capturar información del usuario o confirmar acciones sin salir del contexto actual.
          </p>
        </div>

        <div class="space-y-12">
          {/* Ejemplo Básico */}
          <Card>
            <Card.Header
              title="Modal Básico"
              subtitle="Implementación esencial para mostrar mensajes simples."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="bg-surface-muted p-8 rounded-xl flex flex-col items-center justify-center gap-6 border border-border">
                <Button color="primary" size="lg" onClick={() => setIsBasicOpen(true)}>
                  Abrir Modal Básico
                </Button>

                <Modal isOpen={isBasicOpen()} onClose={() => setIsBasicOpen(false)} closable canMove>
                  <Modal.Header>
                    <h3 class="text-xl font-bold text-foreground">Hola de nuevo</h3>
                  </Modal.Header>
                  <Modal.Body>
                    <p class="text-foreground-subtle leading-relaxed">Este es un modal básico con un diseño limpio. Puedes usarlo para notificaciones rápidas o mensajes informativos.</p>
                  </Modal.Body>
                  <Modal.Footer>
                    <div class="flex justify-end gap-3">
                      <Button variant="text" onClick={() => setIsBasicOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={() => setIsBasicOpen(false)}>Entendido</Button>
                    </div>
                  </Modal.Footer>
                </Modal>
              </div>
            </Card.Body>
          </Card>

          {/* Tamaños */}
          <Card>
            <Card.Header
              title="Múltiples Tamaños"
              subtitle="Ajusta el ancho del modal según la complejidad de tu contenido."
              icon={<Icon icon="material-symbols-light:fullscreen" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap gap-3">
                <For each={sizes}>
                  {size => (
                    <Button
                      color="secondary"
                      variant="outlined"
                      onClick={() => {
                        setSelectedSize(size.value)
                        setIsSizeOpen(true)
                      }}
                    >
                      {size.label}
                    </Button>
                  )}
                </For>

                <Modal isOpen={isSizeOpen()} onClose={() => setIsSizeOpen(false)} size={selectedSize()} closable>
                  <Modal.Header>
                    <div class="flex items-center gap-3">
                      <div class="w-12 h-12 rounded-2xl bg-surface-muted flex items-center justify-center">
                        <Icon icon="material-symbols-light:straighten-outline" class="text-secondary-600" size={28} />
                      </div>
                      <div>
                        <h3 class="text-xl font-bold text-foreground">Variación de Tamaño</h3>
                        <p class="text-sm text-foreground-subtle">
                          Configurado como: <span class="font-mono bg-surface-muted px-1 rounded">{selectedSize()}</span>
                        </p>
                      </div>
                    </div>
                  </Modal.Header>
                  <Modal.Body>
                    <p class="text-foreground-subtle leading-relaxed">
                      Los modales se adaptan automáticamente a diferentes dispositivos. En pantallas pequeñas, se transforman en una hoja inferior (bottom sheet) para una mejor ergonomía táctil.
                    </p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button class="w-full" onClick={() => setIsSizeOpen(false)}>
                      Perfecto
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            </Card.Body>
          </Card>

          {/* Contenido Largo (Scroll) */}
          <Card>
            <Card.Header
              title="Contenido con Scroll"
              subtitle="Ideal para términos y condiciones, documentos legales o formularios extensos."
              icon={<Icon icon="material-symbols-light:description-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="bg-surface-muted p-8 rounded-xl border border-border">
                <Button color="warning" onClick={() => setIsLongContentOpen(true)}>
                  Ver Contenido Extenso
                </Button>

                <Modal isOpen={isLongContentOpen()} onClose={() => setIsLongContentOpen(false)} size="md" closable>
                  <Modal.Header>
                    <h3 class="text-xl font-bold text-foreground">Términos y Condiciones</h3>
                  </Modal.Header>
                  <Modal.Body>
                    <div class="space-y-6 text-foreground-subtle leading-relaxed">
                      <p class="font-semibold text-foreground text-lg">1. Introducción</p>
                      <p>
                        Bienvenido a Florez Jaramillo Solutions. Al utilizar nuestros componentes, usted acepta cumplir con estos términos. Estos términos rigen su uso de nuestras herramientas de
                        interfaz de usuario y cualquier servicio relacionado.
                      </p>

                      <p class="font-semibold text-foreground text-lg">2. Uso de la Licencia</p>
                      <p>
                        Se concede permiso para descargar temporalmente una copia de los materiales para uso personal o comercial. Esta es la concesión de una licencia, no una transferencia de título,
                        y bajo esta licencia usted no puede modificar los materiales de forma que infrinja los derechos de autor.
                      </p>

                      <div class="p-4 bg-surface-muted rounded-lg italic">"Llevamos el desarrollo de interfaces a un nuevo nivel de simplicidad y eficiencia."</div>

                      <p class="font-semibold text-foreground text-lg">3. Privacidad y Datos</p>
                      <p>
                        Su privacidad es importante para nosotros. Recopilamos información solo cuando es estrictamente necesario para brindarle el mejor servicio posible. Todos los datos están
                        protegidos bajo los más altos estándares de cifrado.
                      </p>

                      <p>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation
                        ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
                      </p>

                      <p>
                        Curabitur pretium tincidunt lacus. Nulla gravida orci a odio. Nullam varius, turpis et commodo pharetra, est eros bibendum elit, nec luctus magna felis sollicitudin mauris.
                        Integer in mauris eu nibh euismod gravida. Duis ac tellus et risus vulputate vehicula. Donec lobortis risus a elit. Etiam tempor. Ut ullamcorper, ligula eu tempor congue, eros
                        est euismod turpis, id tincidunt sapien risus a quam. Maecenas fermentum consequat mi. Donec fermentum. Pellentesque malesuada nulla a mi. Duis sapien sem, aliquet nec, commodo
                        eget, consequat quis, neque. Aliquam faucibus, elit ut dictum aliquet, felis nisl adipiscing sapien, sed malesuada diam lacus eget erat. Cras mollis scelerisque nunc. Nullam
                        arcu. Aliquam consequat.
                      </p>
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <div class="flex justify-end">
                      <Button color="warning" onClick={() => setIsLongContentOpen(false)}>
                        He leído y Acepto
                      </Button>
                    </div>
                  </Modal.Footer>
                </Modal>
              </div>
            </Card.Body>
          </Card>

          {/* Comportamiento del Backdrop */}
          <Card>
            <Card.Header
              title="Control del Backdrop"
              subtitle="Configura si el modal debe cerrarse al hacer clic fuera de él."
              icon={<Icon icon="material-symbols-light:lock-outline" class="text-purple-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-col gap-6">
                <div class="flex items-center gap-4 bg-surface-muted p-4 rounded-xl border border-border w-fit">
                  <Switch label="Cerrar al hacer clic en el fondo" checked={closeOnBackdrop()} onChange={e => setCloseOnBackdrop(e.currentTarget.checked)} />
                </div>

                <div>
                  <Button color="secondary" onClick={() => setIsBackdropToggleOpen(true)}>
                    Probar Comportamiento
                  </Button>
                </div>

                <Modal isOpen={isBackdropToggleOpen()} onClose={() => setIsBackdropToggleOpen(false)} closeOnBackdropClick={closeOnBackdrop()} closable>
                  <Modal.Header>
                    <h3 class="text-xl font-bold text-foreground">Ajustes de Interacción</h3>
                  </Modal.Header>
                  <Modal.Body>
                    <p class="text-foreground-subtle">
                      {closeOnBackdrop()
                        ? 'Este modal SE CERRARÁ si haces clic en el fondo oscuro que lo rodea.'
                        : 'Este modal NO SE CERRARÁ aunque hagas clic fuera. Debes usar el botón de cierre o realizar una acción.'}
                    </p>
                  </Modal.Body>
                  <Modal.Footer>
                    <div class="flex justify-end">
                      <Button onClick={() => setIsBackdropToggleOpen(false)}>Entendido</Button>
                    </div>
                  </Modal.Footer>
                </Modal>
              </div>
            </Card.Body>
          </Card>

          {/* Formulario */}
          <Card>
            <Card.Header
              title="Integración con Formularios"
              subtitle="Usa componentes de entrada internos para una experiencia de usuario coherente."
              icon={<Icon icon="material-symbols-light:person-add-outline" class="text-emerald-500" size={24} />}
            />
            <Card.Body>
              <Button color="success" onClick={() => setIsFormOpen(true)} startIcon="material-symbols-light:person-add-outline">
                Crear Nuevo Usuario
              </Button>

              <Modal isOpen={isFormOpen()} onClose={() => setIsFormOpen(false)} size="md" closable>
                <Modal.Header>
                  <h3 class="text-xl font-bold text-foreground text-center">Registrar Usuario</h3>
                </Modal.Header>
                <Modal.Body>
                  <div class="space-y-6">
                    <Input
                      label="Nombre Completo"
                      placeholder="Ej: Juan Pérez"
                      value={formData().name}
                      oninput={e => setFormData(prev => ({ ...prev, name: e.currentTarget.value }))}
                      startIcon="material-symbols-light:person-outline"
                    />
                    <Input
                      label="Correo Electrónico"
                      type="email"
                      placeholder="usuario@ejemplo.com"
                      value={formData().email}
                      oninput={e => setFormData(prev => ({ ...prev, email: e.currentTarget.value }))}
                      startIcon="material-symbols-light:mail-outline"
                    />
                    <div class="bg-success-soft p-4 rounded-lg border border-success/25 flex gap-3 italic text-sm text-on-success-soft">
                      <Icon icon="material-symbols-light:info-outline" size={20} class="shrink-0" />
                      Los datos serán almacenados siguiendo nuestras políticas de seguridad.
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <div class="flex flex-col gap-3">
                    <Button color="success" size="lg" class="w-full" onClick={() => setIsFormOpen(false)}>
                      Guardar Registro
                    </Button>
                    <Button variant="text" color="default" onClick={() => setIsFormOpen(false)}>
                      Cancelar
                    </Button>
                  </div>
                </Modal.Footer>
              </Modal>
            </Card.Body>
          </Card>

          {/* Pantalla Completa */}
          <Card>
            <Card.Header
              title="Modo Pantalla Completa"
              subtitle="Maximiza el espacio para tareas complejas o vistas de edición."
              icon={<Icon icon="material-symbols-light:fullscreen" class="text-blue-500" size={24} />}
            />
            <Card.Body>
              <Button color="info" onClick={() => setIsFullscreenOpen(true)} startIcon="material-symbols-light:fullscreen">
                Lanzar Modo Fullscreen
              </Button>

              <Modal isOpen={isFullscreenOpen()} onClose={() => setIsFullscreenOpen(false)} fullscreen closable>
                <div class="h-full flex flex-col bg-surface-muted">
                  <div class="h-20 bg-surface border-b border-border px-8 flex items-center justify-between sticky top-0 z-10">
                    <div class="flex items-center gap-4">
                      <div class="w-10 h-10 rounded-xl bg-info-soft flex items-center justify-center">
                        <Icon icon="material-symbols-light:settings-outline" class="text-blue-600" size={24} />
                      </div>
                      <h3 class="text-xl font-bold text-foreground">Panel de Configuración Avanzada</h3>
                    </div>
                    <Button variant="text" onClick={() => setIsFullscreenOpen(false)} startIcon="material-symbols-light:close">
                      Cerrar
                    </Button>
                  </div>
                  <div class="flex-1 overflow-y-auto p-8">
                    <Container class="max-w-4xl space-y-8">
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <Card variant="outlined">
                          <Card.Header title="Preferencias" />
                          <Card.Body class="space-y-4">
                            <Switch label="Notificaciones de Sistema" checked />
                            <Switch label="Actualizaciones Automáticas" />
                            <Switch label="Modo Desarrollador" />
                          </Card.Body>
                        </Card>
                        <Card variant="outlined">
                          <Card.Header title="Privacidad" />
                          <Card.Body class="space-y-4">
                            <Switch label="Perfil Público" checked />
                            <Switch label="Mostrar Correo" />
                            <Switch label="Indexar en Google" />
                          </Card.Body>
                        </Card>
                      </div>
                      <div class="p-8 bg-surface rounded-2xl border border-border">
                        <h4 class="text-lg font-bold mb-4">Estadísticas de Uso</h4>
                        <div class="h-64 bg-surface-muted rounded-xl flex items-center justify-center text-foreground-subtle">Panel de Visualización</div>
                      </div>
                    </Container>
                  </div>
                  <div class="p-6 bg-surface border-t border-border flex justify-end gap-4">
                    <Button variant="text" onClick={() => setIsFullscreenOpen(false)}>
                      Cancelar
                    </Button>
                    <Button color="info" onClick={() => setIsFullscreenOpen(false)}>
                      Guardar Cambios
                    </Button>
                  </div>
                </div>
              </Modal>
            </Card.Body>
          </Card>

          {/* Tabla API */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Explora todas las propiedades disponibles para el componente Modal."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4">Propiedad</th>
                      <th class="px-6 py-4">Tipo</th>
                      <th class="px-6 py-4">Default</th>
                      <th class="px-6 py-4">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">isOpen</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Controla si el modal es visible.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">onClose</td>
                      <td class="px-6 py-4 text-foreground-subtle">() =&gt; void</td>
                      <td class="px-6 py-4">-</td>
                      <td class="px-6 py-4 text-foreground-muted">Función que se ejecuta al intentar cerrar el modal.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">size</td>
                      <td class="px-6 py-4 text-foreground-subtle">'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fullscreen'</td>
                      <td class="px-6 py-4">'md'</td>
                      <td class="px-6 py-4 text-foreground-muted">Ancho máximo del modal en pantallas de escritorio.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">closable</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Si es verdadero, muestra un botón de cierre en la esquina superior.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">closeOnBackdropClick</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4">true</td>
                      <td class="px-6 py-4 text-foreground-muted">Cierra el modal al hacer clic en el fondo oscuro.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">closeOnEscape</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4">true</td>
                      <td class="px-6 py-4 text-foreground-muted">Cierra el modal al presionar la tecla "Escape".</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">fullscreen</td>
                      <td class="px-6 py-4 text-foreground-subtle">boolean</td>
                      <td class="px-6 py-4">false</td>
                      <td class="px-6 py-4 text-foreground-muted">Fuerza al modal a ocupar todo el viewport.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono text-primary font-bold">class</td>
                      <td class="px-6 py-4 text-foreground-subtle">string</td>
                      <td class="px-6 py-4">''</td>
                      <td class="px-6 py-4 text-foreground-muted">Clases personalizadas para el panel del modal.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ModalPage
